import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_MARKET_VIEW_LIST_CONFIGURATION, EMPTY_MARKET_VIEW_LIST_CRITERIA } from "../constants/marketViewConstants";
import { MarketViewState } from "../objects/marketViewState";
import { Column } from "../objects/column";
import { setMarketViewPreference } from "../utils/setMarketViewPreference";
import { MarketView } from "../objects/marketView";
import { MarketViewListCriteria } from "../objects/marketViewList";
import { PaymentPolicy } from "../../organisation/objects/paymentPolicy";
import { Currency } from "../../organisation/objects/currency";


const initialState: MarketViewState = {
    market_view_list: [],
    market_view_list_loaded: false,
    market_view_list_refresh: false,
    market_view_list_configuration: EMPTY_MARKET_VIEW_LIST_CONFIGURATION,
    market_view_list_loading: false,
    market_view_list_count: 0,
    market_view_list_criteria: EMPTY_MARKET_VIEW_LIST_CRITERIA,
    market_view_is_creating: false,
    market_view: null,
    market_view_delete: null,
    payment_policy: null,
    currencies: [],
    payment_details_view: null,


    first_preference_loaded: false,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setPreferenceReducer = (state: MarketViewState, action: PayloadAction<Preferences[]>): MarketViewState => {

    const market_view_preference = setMarketViewPreference(action.payload, state.market_view_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        market_view_list_configuration: { ...state.market_view_list_configuration, preferences: market_view_preference.preferences, columns: market_view_preference.columns },
        market_view_list_criteria: { ...state.market_view_list_criteria, order: market_view_preference.order, order_by: market_view_preference.order_by },
    };
};



//  ------------------------------ provider access ----------------------------------- //
const toggleMarketViewRefreshListReducer = (state: MarketViewState): MarketViewState => ({
    ...state, market_view_list_refresh: !state.market_view_list_refresh, market_view_list_loaded: false
});
const addMarketViewListConfigurationPreferenceReducer = (state: MarketViewState, action: PayloadAction<Preferences[]>): MarketViewState => ({
    ...state, market_view_list_configuration: { ...state.market_view_list_configuration, preferences: [...state.market_view_list_configuration.preferences, ...action.payload] }
});
const setMarketViewListConfigurationPreferenceReducer = (state: MarketViewState, action: PayloadAction<Preferences[]>): MarketViewState => ({
    ...state, market_view_list_configuration: { ...state.market_view_list_configuration, preferences: action.payload }
});
const updateMarketViewListReducer = (state: MarketViewState, action: PayloadAction<MarketView[]>): MarketViewState => ({
    ...state, market_view_list: action.payload, market_view_list_loaded: true
});
const updateMarketViewListCriteriaReducer = (state: MarketViewState, action: PayloadAction<MarketViewListCriteria>): MarketViewState => ({
    ...state, market_view_list_criteria: action.payload
});
const updateMarketViewListLoadingReducer = (state: MarketViewState, action: PayloadAction<boolean>): MarketViewState => ({
    ...state, market_view_list_loading: action.payload
});
const updateMarketViewListCountReducer = (state: MarketViewState, action: PayloadAction<number>): MarketViewState => ({
    ...state, market_view_list_count: action.payload
});
const updateMarketViewIsCreatingReducer = (state: MarketViewState, action: PayloadAction<boolean>): MarketViewState => ({
    ...state, market_view_is_creating: action.payload
});
const toggleMarketViewColumnReducer = (state: MarketViewState, action: PayloadAction<number>): MarketViewState => {
    const columns = cloneDeep([...state.market_view_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, market_view_list_configuration: { ...state.market_view_list_configuration, columns } };
};
const setMarketViewColumnsReducer = (state: MarketViewState, action: PayloadAction<Column[]>): MarketViewState => ({
    ...state, market_view_list_configuration: { ...state.market_view_list_configuration, columns: action.payload }
});
const setMarketViewReducer = (state: MarketViewState, action: PayloadAction<MarketView | null>): MarketViewState => ({
    ...state, market_view: action.payload
});
const setMarketViewDeleteReducer = (state: MarketViewState, action: PayloadAction<MarketView | null>): MarketViewState => ({
    ...state, market_view_delete: action.payload
});
const setMarketViewPaymentPolicyReducer = (state: MarketViewState, action: PayloadAction<PaymentPolicy | null>): MarketViewState => ({
    ...state, payment_policy: action.payload
});
const updateMarketViewPaymentPolicyReducer = (state: MarketViewState, action: PayloadAction<Partial<PaymentPolicy>>): MarketViewState => ({
    ...state, payment_policy: state.payment_policy ? { ...state.payment_policy, ...action.payload } as PaymentPolicy : state.payment_policy
});
const setMarketViewCurrenciesReducer = (state: MarketViewState, action: PayloadAction<Currency[]>): MarketViewState => ({
    ...state, currencies: action.payload
});
const setMarketViewPaymentDetailsViewReducer = (state: MarketViewState, action: PayloadAction<'credit_line' | 'wallet' | 'onetime_overflow' | null>): MarketViewState => ({
    ...state, payment_details_view: action.payload
});

export const marketView = createSlice({
    name: 'market_view',
    initialState,
    reducers: {

        toggleMarketViewRefreshList: toggleMarketViewRefreshListReducer,
        addMarketViewListConfigurationPreference: addMarketViewListConfigurationPreferenceReducer,
        setMarketViewListConfigurationPreference: setMarketViewListConfigurationPreferenceReducer,
        updateMarketViewList: updateMarketViewListReducer,
        updateMarketViewListCriteria: updateMarketViewListCriteriaReducer,
        updateMarketViewListLoading: updateMarketViewListLoadingReducer,
        updateMarketViewListCount: updateMarketViewListCountReducer,
        updateMarketViewIsCreating: updateMarketViewIsCreatingReducer,
        setMarketViewColumns: setMarketViewColumnsReducer,
        toggleMarketViewColumn: toggleMarketViewColumnReducer,
        setMarketView: setMarketViewReducer,
        setMarketViewDelete: setMarketViewDeleteReducer,
        setMarketViewPaymentPolicy: setMarketViewPaymentPolicyReducer,
        updateMarketViewPaymentPolicy: updateMarketViewPaymentPolicyReducer,
        setMarketViewCurrencies: setMarketViewCurrenciesReducer,
        setMarketViewPaymentDetailsView: setMarketViewPaymentDetailsViewReducer,
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {

    toggleMarketViewRefreshList,
    addMarketViewListConfigurationPreference,
    setMarketViewListConfigurationPreference,
    updateMarketViewList,
    updateMarketViewListCriteria,
    updateMarketViewListLoading,
    updateMarketViewListCount,
    updateMarketViewIsCreating,
    setMarketViewColumns,
    toggleMarketViewColumn,
    setMarketView,
    setMarketViewDelete,
    setMarketViewPaymentPolicy,
    updateMarketViewPaymentPolicy,
    setMarketViewCurrencies,
    setMarketViewPaymentDetailsView,

} = marketView.actions;